import React, { useState, useMemo, useEffect } from "react";
import { Box, Button, TextField, MenuItem, Select, FormControl, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { axios_tenant } from "../../../../axios";
import { ENTITY_STATUS_OPTIONS, formatEntityStatus, matchesEntityStatusFilter } from "../utils/entityStatus";
import { formatKotDate } from "../utils/dateHelpers";
import { clientSortComparator, toSortableDate } from "../utils/clientSort";
import TruncatedText from "../components/TruncatedText";

const TrackChanges = () => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [entityFilter, setEntityFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchClients = async () => {
    try {
      setLoading(true);

      const response = await axios_tenant.get("/kot/client-profiles", {
        params: { status: "ACTIVE" },
      });

      let data = response?.data?.content || [];

      setClients(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      let matchesSearch = true;

      const cName = client.clientName || client.clientLegalName || "";
      const cNumber = client.clientNumber || "";
      const searchLower = search ? search.toLowerCase() : "";

      if (search) {
        if (searchType === "all") {
          matchesSearch = cName.toLowerCase().includes(searchLower) || cNumber.toLowerCase().includes(searchLower);
        }

        if (searchType === "name") {
          matchesSearch = cName.toLowerCase().includes(searchLower);
        }

        if (searchType === "number") {
          matchesSearch = cNumber.toLowerCase().includes(searchLower);
        }
      }

      const matchesEntity = matchesEntityStatusFilter(client.entityStatus, entityFilter);

      const matchesClient = clientFilter === "" || cName === clientFilter;

      return matchesSearch && matchesEntity && matchesClient;
    });
  }, [clients, search, searchType, entityFilter, clientFilter]);

  const handleCreateNewClient = () => {
    dispatch({ type: "RESET_CLIENT_FORM" });
    navigate("/kot/client/new");
  };

  const handleTrackChanges = (id) => {
    navigate(`/kot/track-changes/${id}`);
  };

  const columns = [
    {
      field: "clientName",
      headerName: "Client Name",
      flex: 1,
      // Sort on the text the cell actually renders, not the raw clientName field.
      valueGetter: (params) => params.row.clientName || params.row.clientLegalName || "",
      sortComparator: clientSortComparator,
      renderCell: (params) => <TruncatedText text={params.row.clientName || params.row.clientLegalName || "-"} maxLength={30} />,
    },
    { field: "clientNumber", headerName: "Client Number", flex: 1, sortComparator: clientSortComparator },
    {
      field: "entityStatus",
      headerName: "Entity Status",
      flex: 1,
      renderCell: (params) => <span>{formatEntityStatus(params.row.entityStatus)}</span>,
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 1,
      // The payload field is lastUpdatedAt, so without this the column sorts an undefined
      // value on every row and clicking the header does nothing. dateTime because the value
      // carries a time, which decides the order of same-day edits.
      type: "dateTime",
      valueGetter: (params) => toSortableDate(params.row.lastUpdatedAt),
      renderCell: (params) => <span>{formatKotDate(params.row.lastUpdatedAt)}</span>,
    },
    {
      field: "viewChanges",
      headerName: "View Changes",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button onClick={() => handleTrackChanges(params.row.id)} sx={{ textAlign: "center" }}>
          Track Changes
        </Button>
      ),
    },
  ];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Box display="flex">
          <FormControl size="small">
            <Select value={searchType} onChange={(e) => setSearchType(e.target.value)} sx={{ borderRadius: "0", borderTopLeftRadius: "50px", borderBottomLeftRadius: "50px" }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="name">Client Name</MenuItem>
              <MenuItem value="number">Client Number</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                borderTopRightRadius: "50px",
                borderBottomRightRadius: "50px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl size="small">
            <Select displayEmpty value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <MenuItem value="">Client Name</MenuItem>
              {[...new Set(clients.map((c) => c.clientName || c.clientLegalName).filter(Boolean))].map((name, index) => (
                <MenuItem key={`${name}-${index}`} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select displayEmpty value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
              <MenuItem value="">Entity Status</MenuItem>
              {ENTITY_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNewClient}>
            Create New Client
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          height: 700,
          width: "100%",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 2,
        }}
      >
        <DataGrid
          sx={{
            border: 0,

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: "bold",
              borderBottom: "1px solid #ddd",
            },

            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #eee",
              overflow: "hidden",
            },
            "& .MuiDataGrid-cell > span": {
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            },

            "& .MuiDataGrid-columnSeparator": {
              visibility: "visible",
            },
            // Custom pagination styles
            ".MuiTablePagination-displayedRows": {
              marginTop: "1em",
              marginBottom: "1em",
            },
            ".MuiTablePagination-selectLabel": {
              marginTop: "1em",
              marginBottom: "1em",
            },
          }}
          rows={filteredClients}
          columns={columns}
          checkboxSelection
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          pageSize={8}
          rowsPerPageOptions={[8]}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default TrackChanges;
